import { buildLabel } from 'src/utilities/naming';
import { meaningfulNameInEmail } from 'src/utilities/dashboard';
import { set, lensPath, pathOr, gte, split, any, includes, not } from 'ramda';
import { compose } from 'recompose';
import { getNamespaceName, getNamespaceId } from 'src/services/objects/namespaces';
import {
  USER_ONBOARD_APPLICANT
} from 'src/services/constants/accounts';
import { mkObject } from 'src/services/objects/build'
import { DEFAULT_PASSWORD, META_DATA, STATUS_ACTIVE } from 'src/services/constants';
import { NO, YES } from 'src/constants';
import { AT_EMAIL_COM } from 'src/features/constants';
import { calculateDaysPassedSinceGivenDate } from 'src/utilities/formatDate';
import siteSetting from 'src/siteSetting';
import { isNotEmpty } from 'src/utilities/hasValue';


export const mkForgotPassword = (value: string) => {
  return {
    email: value
  };
};

/**
mkAccount is used to make basic account object builder
@param email - string email address of the user
@param password - string password user security
@param otherArgs - getting optional data ex: name
*/
export const mkUserAccount = (email: string, password?: string, otherArgs?: any) => {
  const firstName = pathOr(meaningfulNameInEmail(email), ['applicantProfile', 'first_name'], otherArgs);
  const lastName = pathOr('', ['applicantProfile', 'last_name'], otherArgs);
  return {
    object_meta: mkObject(email),
    email,
    password: password,
    first_name: firstName,
    last_name: lastName,
    cellmobile_phone: pathOr('', ['cellmobile_phone'], otherArgs),
    roles_id: [USER_ONBOARD_APPLICANT],
    namespace_id: getNamespaceId(),
    metadata: {
      [buildLabel('marketing_alerts')]: YES,
      [buildLabel('sms_alerts')]: YES,
    },
    sso: {
      is_enable_sso: Boolean(false)
    }
  }
};

// Some of the fields in both methods are repeating
const mkAccountWithPhoneNumber = (namespaceId: string, phoneNumber: string, metadataObj: any) => {
  return compose(
    set(lensPath(['email']), `appstarter${Date.now()}${AT_EMAIL_COM}`),
    set(lensPath(['cellmobile_phone']), phoneNumber),
    set(lensPath(['password']), DEFAULT_PASSWORD),
    set(lensPath(['confirmPassword']), DEFAULT_PASSWORD),
    set(lensPath(['first_name']), ''),
    set(lensPath(['is_admin']), false),
    set(lensPath(['roles_id']), [USER_ONBOARD_APPLICANT]),
    set(lensPath(['object_meta', 'namespace']), getNamespaceName()),
    set(lensPath(['status']), STATUS_ACTIVE),
    set(lensPath(['namespace_id']), namespaceId),
    set(lensPath([META_DATA, buildLabel('marketing_alert')]), NO),
    set(lensPath([META_DATA, buildLabel('sms_alert')]), YES),
    set(lensPath([META_DATA, buildLabel('traits_email')]), YES),
  )(metadataObj)
}


const mkAccountWithEmail = (namespaceId: string, email: string, metadataObj: Object) => {
  return compose(
    set(lensPath(['email']), email),
    set(lensPath(['cellmobile_phone']), ''),
    set(lensPath(['password']), DEFAULT_PASSWORD),
    set(lensPath(['confirmPassword']), DEFAULT_PASSWORD),
    set(lensPath(['first_name']), ''),
    set(lensPath(['is_admin']), false),
    set(lensPath(['roles_id']), [USER_ONBOARD_APPLICANT]),
    set(lensPath(['object_meta', 'namespace']), getNamespaceName()),
    set(lensPath(['status']), STATUS_ACTIVE),
    set(lensPath(['namespace_id']), namespaceId),
    set(lensPath([META_DATA, buildLabel('marketing_alert')]), YES),
    set(lensPath([META_DATA, buildLabel('sms_alert')]), YES),
    set(lensPath([META_DATA, buildLabel('traits_email')]), YES),
  )(metadataObj)
}

export const mkAccountForAppStarter = (namespaceId: string, emailOrPhoneNumber: string, containsEmail: boolean, metadataObj: Object) => {
  if (containsEmail) {
    return mkAccountWithEmail(namespaceId, emailOrPhoneNumber, metadataObj)
  }
  return mkAccountWithPhoneNumber(namespaceId, emailOrPhoneNumber, metadataObj)
}

export const updateExpiredPassword = (accountDetails: any, password: string) => {
  return compose(
    set(lensPath(['id']), pathOr('', ['id'], accountDetails)),
    set(lensPath(['email']), pathOr('', ['email'], accountDetails)),
    set(lensPath(['password']), password),
  )({});


}
/**
 * 
 * @param userDetail account details
 * @returns if the number of days that have passed since the password updation is greater than expiry date
 * it will return true 
 */
export const isAccountPasswordExpired = (userDetail: any) => {
  const passwordUpdatedAt = pathOr('', ['password_updated_at'], userDetail);
  const expiryDays = siteSetting.generalPasswordExpiryDays()
  const skipRoles = split(",", siteSetting.generalPasswordExpirySkipRoles()) || []
  const accountRoles = pathOr([], ['roles_id'], userDetail);
  const daysPassed = Math.round((calculateDaysPassedSinceGivenDate(passwordUpdatedAt)))
  return isNotEmpty(expiryDays) && not(any((role: string) => includes(role, accountRoles), skipRoles)) ? gte(daysPassed, Number(expiryDays)) : false
}